import React, { useEffect, useState } from 'react';
import { RefreshCw, Tag, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useCurrency } from '../contexts/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Monthly subscription toggle for the product page.
 *
 * Renders two pill-shaped cards, "One-time" and "Monthly (-10%)", and
 * when subscription mode is selected, changes the primary CTA to a
 * "Subscribe monthly" button that initiates a Stripe subscription
 * checkout and redirects the user to the hosted Stripe page.
 *
 * Returns `{ mode, SubscribeButton }` via render-prop so the parent
 * can place the subscribe button in the same spot as Add-to-Cart and
 * show whichever matches the selected mode.
 */
const SubscriptionToggle = ({
  productId,
  variantLabel,
  currentLanguage,
  mode,
  onModeChange,
  className = '',
}) => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const url = `${API}/api/subscriptions/pricing/${productId}${
          variantLabel ? `?variant_label=${encodeURIComponent(variantLabel)}` : ''
        }`;
        const res = await axios.get(url);
        if (!cancelled) setPricing(res.data);
      } catch {
        if (!cancelled) setPricing(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [productId, variantLabel]);

  if (loading) {
    return (
      <div className={`text-xs text-gray-400 ${className}`}>
        <Loader2 className="w-3 h-3 animate-spin inline" /> Loading subscription options...
      </div>
    );
  }
  if (!pricing) return null;

  const labels = {
    en: {
      purchaseType: 'Choose your purchase type',
      oneTime: 'One-time',
      monthly: 'Monthly Subscription',
      offTag: 'SAVE 10%',
      monthlyHint: 'Auto-renew every 30 days · cancel anytime',
      youSave: 'You save',
    },
    es: {
      purchaseType: 'Elige el tipo de compra',
      oneTime: 'Compra única',
      monthly: 'Suscripción mensual',
      offTag: 'AHORRA 10%',
      monthlyHint: 'Renovación automática cada 30 días · cancela cuando quieras',
      youSave: 'Ahorras',
    },
    ru: {
      purchaseType: 'Выберите тип покупки',
      oneTime: 'Разовая покупка',
      monthly: 'Ежемесячная подписка',
      offTag: 'СКИДКА 10%',
      monthlyHint: 'Автопродление каждые 30 дней · отмена в любой момент',
      youSave: 'Экономия',
    },
  }[currentLanguage] || {};

  return (
    <div className={`space-y-3 ${className}`} data-testid="subscription-toggle">
      <div className="text-sm font-medium text-gray-700">{labels.purchaseType}</div>
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => onModeChange('one_time')}
          data-testid="mode-one-time"
          className={`relative text-left rounded-2xl border-2 p-4 transition ${
            mode === 'one_time'
              ? 'border-capsule-teal bg-gradient-to-br from-capsule-teal/10 to-capsule-aqua/10 shadow-md'
              : 'border-gray-200 bg-white hover:border-capsule-teal/40'
          }`}
        >
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
            {labels.oneTime}
          </div>
          <div className="text-xl font-bold text-gray-900 mt-1">
            {formatPrice(pricing.one_time_price_eur)}
          </div>
        </button>

        <button
          type="button"
          onClick={() => onModeChange('subscription')}
          data-testid="mode-subscription"
          className={`relative text-left rounded-2xl border-2 p-4 transition ${
            mode === 'subscription'
              ? 'border-capsule-coral bg-gradient-to-br from-capsule-coral/10 to-capsule-salmon/10 shadow-md'
              : 'border-gray-200 bg-white hover:border-capsule-coral/40'
          }`}
        >
          <span
            className="absolute -top-2 -right-2 bg-gradient-to-r from-capsule-coral to-capsule-salmon text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md flex items-center gap-1"
            data-testid="subscription-save-badge"
          >
            <Tag className="w-3 h-3" /> {labels.offTag}
          </span>
          <div className="text-xs uppercase tracking-wider text-capsule-coral font-semibold flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> {labels.monthly}
          </div>
          <div className="text-xl font-bold text-gray-900 mt-1">
            {formatPrice(pricing.subscription_price_eur)}
            <span className="text-sm font-normal text-gray-500">/mo</span>
          </div>
          <div className="text-[11px] text-gray-500 line-through">
            {formatPrice(pricing.one_time_price_eur)}
          </div>
        </button>
      </div>
      {mode === 'subscription' && (
        <div className="text-xs text-gray-600 bg-capsule-coral/5 border border-capsule-coral/20 rounded-xl px-3 py-2">
          💚 {labels.monthlyHint} · {labels.youSave} {formatPrice(pricing.you_save_eur)}
        </div>
      )}
    </div>
  );
};

export default SubscriptionToggle;
